/**
 * The colours of StockVol, in one place: the Mantine theme, the plan view, the charts and the 3D
 * view all read them from here (nothing imports Mantine, echarts or three in this file), so a
 * dark variant is one more `Palette` and a switch of `palette`.
 */

type Shades = readonly [string, string, string, string, string, string, string, string, string, string];

export interface Palette {
  scheme: "light" | "dark";
  canvas: string;
  surface: string;
  surfaceAlt: string;
  border: string;
  text: string;
  muted: string;
  grid: string;
  side: { bg: string; hover: string; active: string; border: string; text: string; muted: string };
  brand: Shades;
  /** categorical colours, a fixed order (products are mapped onto them, never cycled) */
  series: readonly string[];
  noProduct: string;
  change: { stacked: string; reclaimed: string; unexplained: string };
  plan: {
    yard: string;
    yardStroke: string;
    coverage: string;
    spoke: string;
    ring: string;
    label: string;
    halo: string;
    boom: string;
    pivot: string;
  };
  scene: { top: string; bottom: string; outline: string; chipBg: string; chipText: string };
}

export const light: Palette = {
  scheme: "light",
  canvas: "#F3F5F8",
  surface: "#FFFFFF",
  surfaceAlt: "#F8FAFC",
  border: "#E2E7EE",
  text: "#15202B",
  muted: "#5E6B7A",
  grid: "#E6EAF0",
  side: {
    bg: "#0B1B2B",
    hover: "#12283D",
    active: "#183450",
    border: "#1C3146",
    text: "#C2CFDC",
    muted: "#7E92A6",
  },
  brand: [
    "#E7F5FB",
    "#CDE9F5",
    "#9CD2EA",
    "#66BADF",
    "#3BA5D4",
    "#1F97CC",
    "#0C86BD",
    "#0672A3",
    "#005F8A",
    "#004D72",
  ],
  series: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
  noProduct: "#8C99A6",
  change: { stacked: "#e34948", reclaimed: "#2a78d6", unexplained: "#9AA5B1" },
  plan: {
    yard: "#F4F6F9",
    yardStroke: "#C5CDD8",
    coverage: "#DCEEF8",
    spoke: "#D5DBE3",
    ring: "#C9D1DB",
    label: "#15202B",
    halo: "#FFFFFF",
    boom: "#0672A3",
    pivot: "#15202B",
  },
  scene: {
    top: "#1B2836",
    bottom: "#0A121B",
    outline: "#E8EEF4",
    chipBg: "rgba(11, 27, 43, 0.72)",
    chipText: "#E8EEF4",
  },
};

export const palette: Palette = light;

/**
 * A product's colour, stable for a given set of products: names are sorted and take the series
 * slots in order (a ninth product and a pile without one get `noProduct`).
 */
export function productColours(products: Iterable<string | null | undefined>): (p: string | null | undefined) => string {
  const names = [...new Set([...products].filter((p): p is string => !!p))].sort();
  const slot = new Map(names.map((n, i) => [n, palette.series[i] ?? palette.noProduct]));
  return (p) => (p ? (slot.get(p) ?? palette.noProduct) : palette.noProduct);
}
